import { Route, Routes, useLocation, Navigate } from 'react-router';
import SubmitInsurancePage from '../containers/SubmitInsurancePage';
import { Pages } from '../shared/pages';
import LogCannon from '@ace-clublabs/log-cannon';
import { useEffect } from 'react';
import VerifyPolicyInfoPage from '../containers/VerifyPolicyInfoPage';
import PaymentAmountSelectionPage from '../containers/PaymentAmountSelectionPage';
import UpdateEmailsPage from '../containers/UpdateEmailsPage';
import { useGlobalState, createEmptyInitialState } from '../shared/state';
import { SorryPage } from '../containers/SorryPage';
import ConfirmationPage from '../containers/ConfirmationPage';
import { constructAllowedUrl } from '../utils/methods';
import { allowedNonACEClubs } from '../shared/constants';
import { AutoPayPage } from '../containers/AutoPayPage';
import PaymentStatusPage from '../containers/PaymentStatusPage';
import { AlreadyPaidPage } from '../containers/AlreadyPaidPage';
import { Maintenance } from './Maintenance';
import { ErrorType } from '../shared/types';

const Router: React.FC = () => {
  let location = useLocation();
  useEffect(() => {
    LogCannon.blast(`Navigate to ${location.pathname}`, {
      pathname: location.pathname,
      search: location.search,
    });
  }, [location]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const state = {
    appFlow: useGlobalState('appFlow')[0],
    policyPaymentInfo: useGlobalState('policyPaymentInfo')[0],
    policyNumber: useGlobalState('policyNumber')[0],
    primaryEmail: useGlobalState('primaryEmail')[0],
    secondaryEmail: useGlobalState('secondaryEmail')[0],
    amountChosen: useGlobalState('amountChosen')[0],
    paymentPlan: useGlobalState('paymentPlan')[0],
    transactionId: useGlobalState('transactionId')[0],
    clubCode: useGlobalState('clubCode')[0],
    selectedAutoPay: useGlobalState('selectedAutoPay')[0],
    clubTag: useGlobalState('clubTag')[0],
    startPath: useGlobalState('startPath')[0],
    alreadyPaidOption: useGlobalState('alreadyPaidOption')[0],
    policyMetaData: useGlobalState('policyMetaData')[0],
    isMobileApp: useGlobalState('isMobileApp')[0],
    inMaintenance: useGlobalState('inMaintenance')[0],
    brokerRegion: useGlobalState('brokerRegion')[0],
  };

  let baseUrl: string = Pages.SubmitInsurance;
  let path = location.pathname;
  let locationState = { ...(location.state as { [prop: string]: any }) };

  const sorryPageProps = { errorTag: locationState?.errorTag, errorType: locationState?.errorType as ErrorType };
  const maintenancePageProps = { startDate: locationState?.startDate, endDate: locationState?.endDate };

  baseUrl = constructAllowedUrl(allowedNonACEClubs, path);

  useEffect(() => {
    if (path === baseUrl) {
      const _state = {
        ...createEmptyInitialState(),
        startPath: state.startPath,
      };
      sessionStorage.setItem('insurancePwolState', JSON.stringify(_state));
    } else {
      sessionStorage.setItem('insurancePwolState', JSON.stringify({ ...state }));
    }
  }, [state, path]);

  if (!state.policyNumber && path !== baseUrl && !state.inMaintenance) {
    return (
      <Routes>
        <Route path="*" element={<Navigate to={baseUrl} />} />
        <Route path={baseUrl} element={<SubmitInsurancePage />} />
      </Routes>
    );
  }

  return (
    <Routes>
      <Route path={baseUrl} element={<SubmitInsurancePage />} />
      <Route path={Pages.VerifyPolicy} element={<VerifyPolicyInfoPage />} />
      <Route path={Pages.PaymentSelection} element={<PaymentAmountSelectionPage />} />
      <Route path={Pages.PaymentStatus} element={<PaymentStatusPage />} />
      <Route path={Pages.UpdateEmail} element={<UpdateEmailsPage />} />
      <Route path={Pages.Confirmation} element={<ConfirmationPage />} />
      <Route path={Pages.AutoPay} element={<AutoPayPage />} />
      <Route path={Pages.SorryPage} element={<SorryPage {...sorryPageProps} />} />
      <Route path={Pages.AlreadyPaid} element={<AlreadyPaidPage />} />
      <Route path={Pages.Maintenance} element={<Maintenance {...maintenancePageProps} />} />
      <Route path="*" element={<SorryPage />} />
    </Routes>
  );
};

export default Router;
